// The conversation table: sortable on every column (server-side), one row per
// conversation with its stream, media, owner, activity and aggregates, and a
// pager. Clicking a row opens the transcript drawer; the Console link opens
// the thread in the full Agent Console in a new tab.

import type { MonitorPage, MonitorRow, MonitorSortKey } from '../../services/merlinMonitor';
import { formatDateTime, formatRelativeTime } from '../../utils/dateFormatters';
import { formatMoney } from '../../utils/formatting';
import { ChannelChips, StateBadge, StreamBadge } from './StreamBadge';
import { rowTitle } from './monitorDisplay';
import { PAGE_SIZES, pageCount, type MonitorFilterState } from './monitorFilters';
import styles from './MonitorTable.module.css';

export interface MonitorTableProps {
  page: MonitorPage | null;
  state: MonitorFilterState;
  loading: boolean;
  error: string | null;
  selectedId: string | null;
  onSort: (key: MonitorSortKey) => void;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
  onOpen: (row: MonitorRow) => void;
}

interface Column {
  key: MonitorSortKey | null;
  label: string;
  className?: string;
  title?: string;
}

const COLUMNS: Column[] = [
  { key: 'last_activity', label: 'Last activity' },
  { key: 'title', label: 'Conversation' },
  { key: 'profile', label: 'Stream · agent' },
  { key: 'owner', label: 'Started by' },
  { key: 'state', label: 'State' },
  { key: 'messages', label: 'Msgs', className: styles.num, title: 'Messages (Merlin replies)' },
  { key: 'tool_calls', label: 'Tools', className: styles.num, title: 'Tool calls (errors)' },
  { key: 'tokens', label: 'Tokens', className: styles.num, title: 'Input + output tokens' },
  { key: 'cost', label: 'Cost', className: styles.num },
  { key: 'feedback_down', label: 'Feedback', className: styles.num, title: '👍 / 👎' },
  { key: 'model', label: 'Model' },
  { key: null, label: '' },
];

export function MonitorTable({
  page,
  state,
  loading,
  error,
  selectedId,
  onSort,
  onPage,
  onPageSize,
  onOpen,
}: MonitorTableProps) {
  const total = page?.total ?? 0;
  const pages = pageCount(total, state.pageSize);
  const from = total === 0 ? 0 : state.page * state.pageSize + 1;
  const to = Math.min(total, (state.page + 1) * state.pageSize);

  return (
    <div className={styles.wrap} data-testid="monitor-table">
      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {COLUMNS.map((c) => {
                const active = c.key !== null && state.sortBy === c.key;
                return (
                  <th
                    key={c.label || 'actions'}
                    className={`${c.className ?? ''} ${c.key ? styles.sortable : ''} ${active ? styles.sorted : ''}`}
                    title={c.title}
                    aria-sort={active ? (state.sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {c.key ? (
                      <button
                        type="button"
                        className={styles.sortBtn}
                        onClick={() => onSort(c.key as MonitorSortKey)}
                        data-testid={`sort-${c.key}`}
                      >
                        {c.label}
                        <span className={styles.sortMark} aria-hidden="true">
                          {active ? (state.sortDir === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    ) : (
                      c.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className={loading ? styles.loading : ''}>
            {error && (
              <tr>
                <td colSpan={COLUMNS.length} className={styles.msg} data-testid="table-error">
                  {error}
                </td>
              </tr>
            )}
            {!error && page && page.items.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className={styles.msg}>
                  No conversations match these filters.
                </td>
              </tr>
            )}
            {!error && !page && (
              <tr>
                <td colSpan={COLUMNS.length} className={styles.msg}>
                  Loading…
                </td>
              </tr>
            )}
            {page?.items.map((row) => (
              <tr
                key={row.id}
                className={`${styles.row} ${selectedId === row.id ? styles.rowOn : ''}`}
                onClick={() => onOpen(row)}
                data-testid="monitor-row"
                data-conversation-id={row.id}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(row);
                  }
                }}
              >
                <td className={styles.when} title={formatDateTime(row.last_activity_at)}>
                  {formatRelativeTime(row.last_activity_at)}
                  <span className={styles.whenSub}>{formatDateTime(row.created_at)}</span>
                </td>
                <td className={styles.titleCell}>
                  <span className={styles.title}>{rowTitle(row)}</span>
                  {row.title && row.first_user_message && (
                    <span className={styles.preview}>{row.first_user_message}</span>
                  )}
                  {row.error_message && (
                    <span className={styles.err} title={row.error_message}>
                      ⚠ {row.error_message}
                    </span>
                  )}
                </td>
                <td>
                  <StreamBadge stream={row.stream} label={row.stream_label} />
                  <span className={styles.agent}>
                    {row.profile_name ?? '—'}
                  </span>
                  <ChannelChips channels={row.channels} />
                  {row.email_thread_id && (
                    <span className={styles.threadTag} title={`Bound to Gmail thread ${row.email_thread_id}`}>
                      ✉ thread
                    </span>
                  )}
                </td>
                <td>
                  <span className={styles.owner}>{row.owner_name ?? row.owner_email ?? '—'}</span>
                  {row.owner_name && row.owner_email && (
                    <span className={styles.ownerSub}>{row.owner_email}</span>
                  )}
                </td>
                <td>
                  <StateBadge state={row.state} />
                </td>
                <td className={styles.num}>
                  {row.message_count.toLocaleString()}
                  <span className={styles.numSub}>{row.assistant_turns} replies</span>
                </td>
                <td className={styles.num}>
                  {row.tool_call_count.toLocaleString()}
                  {row.tool_errors > 0 && (
                    <span className={styles.numBad}>{row.tool_errors} err</span>
                  )}
                </td>
                <td className={styles.num}>
                  {(row.input_tokens + row.output_tokens).toLocaleString()}
                  {row.cache_read_input_tokens + row.cache_creation_input_tokens > 0 && (
                    <span className={styles.numSub}>
                      +{(row.cache_read_input_tokens + row.cache_creation_input_tokens).toLocaleString()} cached
                    </span>
                  )}
                </td>
                <td className={styles.num}>{formatMoney(row.cost_usd)}</td>
                <td className={styles.num}>
                  {row.feedback_up + row.feedback_down === 0 ? (
                    <span className={styles.numSub}>—</span>
                  ) : (
                    <>
                      <span className={styles.up}>👍{row.feedback_up}</span>{' '}
                      <span className={styles.down}>👎{row.feedback_down}</span>
                    </>
                  )}
                </td>
                <td className={styles.model}>{row.model.replace('claude-', '')}</td>
                <td className={styles.actions}>
                  <a
                    className={styles.link}
                    href={`/agent-console?conversation=${row.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    title="Open in the Agent Console (new tab)"
                  >
                    Console ↗
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.pager} data-testid="monitor-pager">
        <span className={styles.pagerInfo}>
          {total === 0 ? 'No rows' : `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`}
        </span>
        <label className={styles.pageSize}>
          Rows
          <select value={state.pageSize} onChange={(e) => onPageSize(Number(e.target.value))}>
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <div className={styles.pagerBtns}>
          <button type="button" disabled={state.page === 0} onClick={() => onPage(0)} aria-label="First page">
            «
          </button>
          <button
            type="button"
            disabled={state.page === 0}
            onClick={() => onPage(state.page - 1)}
            aria-label="Previous page"
            data-testid="page-prev"
          >
            ‹
          </button>
          <span className={styles.pageNo}>
            Page {state.page + 1} of {pages}
          </span>
          <button
            type="button"
            disabled={state.page + 1 >= pages}
            onClick={() => onPage(state.page + 1)}
            aria-label="Next page"
            data-testid="page-next"
          >
            ›
          </button>
          <button
            type="button"
            disabled={state.page + 1 >= pages}
            onClick={() => onPage(pages - 1)}
            aria-label="Last page"
          >
            »
          </button>
        </div>
      </div>
    </div>
  );
}
